import React, { Fragment, FunctionComponent } from "react";
import styles from "./MegaDropdown.module.scss";

import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import megaDropdownHandler from "../helpers/megaDropdownHandler";
import MenuLink from "blocks/models/menu-link.interface";
import navigationIsActive from "../helpers/navigation-is-active";
import useWindowDimensions from "hooks/useWindowDimensions";
import combineClasses from "helpers/combine-classes";
import getVideoHeightRatio from "app/media/helpers/getVideoHeightRatio";
import VideoViewer from "components/VideoViewer/VideoViewer";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";

const MegaDropdown: FunctionComponent<MenuLink> = (props) => {
    const { width } = useWindowDimensions();
    const dispatch = useDispatch();

    const imageCrop = getImageCrop(props.content.media?.crops, undefined, width > 768 ? "Square (1:1)" : "Widescreen (16:9)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.media?.width, props.content.media?.height);
    const videoHeightRatio = getVideoHeightRatio("Widescreen (16:9)");

    return (
        <Fragment>
            {/* Lock the scrollbar for the rest of the page on mobile, while drawer is open */}
            {/* TODO: Remember the scroll position and restore it when toggling locked state */}
            <Helmet bodyAttributes={{ class: "is-locked" }} />

            <div
                className={styles.wrapper}
                onMouseEnter={(event) => megaDropdownHandler(event, props, dispatch)}
                onMouseLeave={(event) => megaDropdownHandler(event, undefined, dispatch)}
            >
                <div className={styles.inner}>
                    {/* Banner */}
                    {props.content.media ? (
                        <Anchor
                            key={props.contentUdi}
                            className={styles.media_link}
                            to={props.content.link}
                        >
                            <div className={styles.media_content}>
                                {/* Media - Tagline */}
                                <span className={styles.tagline}>{props.content?.mediaTagline}</span>

                                {/* Media - Headline */}
                                <h3 className={styles.headline}>{props.content?.mediaHeadline}</h3>

                                <IconViewer
                                    className={styles.arrow}
                                    name="arrowRight"
                                    heightRatio={10.848 / 31.519}
                                />
                            </div>

                            {/* Media */}
                            <div className={styles.media}>
                                {(() => {
                                    if (!props.content.media) return null;

                                    const mediaUrl = (typeof props.content.media === "string" ? props.content.media : props.content.media?.url) || "";
                                    const fileExtension = mediaUrl.split(".").pop()?.toLowerCase() || "";
                                    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
                                    const videoExtensions = ["mp4", "webm", "mov", "avi", "mkv", "ogg"];

                                    if (imageExtensions.includes(fileExtension)) {
                                        return (
                                            <ImageViewer
                                                className={styles.image}
                                                src={mediaUrl}
                                                crop={imageCrop}
                                                focalPoint={props.content.media?.focalPoint}
                                                {...imageAspectRatio}
                                                alt={""}
                                            />
                                        );
                                    }

                                    if (videoExtensions.includes(fileExtension)) {
                                        return (
                                            <VideoViewer
                                                className={styles.video}
                                                key={props.content.media._id}
                                                src={mediaUrl}
                                                heightRatio={videoHeightRatio}
                                                alt={""}
                                                autoplay={true}
                                                loop={true}
                                                soundButton={false}
                                            />
                                        );
                                    }

                                    return null;
                                })()}
                            </div>
                        </Anchor>
                    ) : null}

                    {!props.content.media ? <div className={styles.no_media}></div> : null}

                    {/* Content */}
                    <div className={styles.content}>
                        {/* Navigation */}
                        <nav className={styles.navigation}>
                            {props.content?.subMenu &&
                                props.content?.subMenu
                                    .filter((item) => item !== null)
                                    .map((item) => {
                                        return (
                                            <Anchor
                                                key={item.contentUdi}
                                                className={combineClasses([styles.navigation_link, navigationIsActive(item, styles)])}
                                                to={item?.link}
                                            >
                                                <span>{item.label}</span>
                                            </Anchor>
                                        );
                                    })}
                        </nav>

                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                height: "100%",
                                zIndex: 12,
                                pointerEvents: "none",
                                display: "flex",
                                justifyContent: "space-between",
                            }}
                        >
                            {/* Left curve */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 692 1220"
                                fill="none"
                                className={styles.left_curve}
                            >
                                <path
                                    d="M0.31651 -1.38375e-08L877.546 -3.83588e-05C877.546 -3.83588e-05 882.496 33.5764 884.655 48.3075C885.691 58.6364 889.691 86.1419 893.547 109.763C894.554 117.33 897.288 134.191 899.648 147.397C901.029 155.913 904.339 174.471 907.044 188.914C908.541 198.265 912.455 218.434 915.822 233.941C917.606 244.011 922.009 265.533 925.664 281.99C930.384 303.223 940.744 349.488 948.773 385.309C954.155 408.729 965.522 458.273 974.127 495.964C980.027 520.736 993.035 571.719 1003.16 609.871C1008.95 631.19 1021.04 674.29 1030.16 706.083C1038.5 735.084 1054.88 791.908 1066.74 832.994C1074.02 856.932 1086.91 903.081 1095.54 936.025C1101.7 958.495 1112.15 1000.21 1118.83 1029.24C1123.75 1049.38 1130.94 1085.03 1134.88 1108.88C1137.88 1125.89 1141.59 1153.77 1143.17 1171.15C1144.38 1184.18 1145.24 1202.25 1145.1 1211.51L1145.1 1220L268.158 1220C268.158 1220 260.561 1182.48 258.546 1172.41C257.107 1165.1 252.877 1146.83 249.049 1131.64C248.071 1127.67 245.826 1119.07 244.013 1112.39C240.128 1098.01 230.084 1063.54 221.623 1035.4C216.155 1017.48 203.234 976.88 192.73 944.714C185.852 923.998 171.318 878.568 160.354 843.265C153.159 820.219 138.943 771.106 128.611 733.531C121.733 708.758 107.632 657.689 97.1562 619.48C81.8748 567.317 57.0678 465.955 41.7864 393.077C36.117 368.564 27.3395 321.321 22.1594 287.6C20.2025 276.351 16.9793 254.369 14.9648 238.401C13.3532 227.727 10.8207 207.069 9.29541 192.28C8.02916 182.325 6.21611 163.279 5.18008 149.785C4.14406 140.635 2.8778 123.43 2.38857 111.288C1.72666 103.06 0.949641 87.9547 0.661856 77.5394C0.517963 70.2889 0.230177 57.428 -5.11937e-05 48.8253C0.201398 35.8206 0.287732 2.21545 0.31651 -1.38375e-08Z"
                                    fill="#223d34"
                                />
                            </svg>

                            {/* Right curve */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 692 1220"
                                fill="none"
                                className={styles.right_curve}
                            >
                                <path
                                    d="M0.31651 -1.38375e-08L877.546 -3.83588e-05C877.546 -3.83588e-05 882.496 33.5764 884.655 48.3075C885.691 58.6364 889.691 86.1419 893.547 109.763C894.554 117.33 897.288 134.191 899.648 147.397C901.029 155.913 904.339 174.471 907.044 188.914C908.541 198.265 912.455 218.434 915.822 233.941C917.606 244.011 922.009 265.533 925.664 281.99C930.384 303.223 940.744 349.488 948.773 385.309C954.155 408.729 965.522 458.273 974.127 495.964C980.027 520.736 993.035 571.719 1003.16 609.871C1008.95 631.19 1021.04 674.29 1030.16 706.083C1038.5 735.084 1054.88 791.908 1066.74 832.994C1074.02 856.932 1086.91 903.081 1095.54 936.025C1101.7 958.495 1112.15 1000.21 1118.83 1029.24C1123.75 1049.38 1130.94 1085.03 1134.88 1108.88C1137.88 1125.89 1141.59 1153.77 1143.17 1171.15C1144.38 1184.18 1145.24 1202.25 1145.1 1211.51L1145.1 1220L268.158 1220C268.158 1220 260.561 1182.48 258.546 1172.41C257.107 1165.1 252.877 1146.83 249.049 1131.64C248.071 1127.67 245.826 1119.07 244.013 1112.39C240.128 1098.01 230.084 1063.54 221.623 1035.4C216.155 1017.48 203.234 976.88 192.73 944.714C185.852 923.998 171.318 878.568 160.354 843.265C153.159 820.219 138.943 771.106 128.611 733.531C121.733 708.758 107.632 657.689 97.1562 619.48C81.8748 567.317 57.0678 465.955 41.7864 393.077C36.117 368.564 27.3395 321.321 22.1594 287.6C20.2025 276.351 16.9793 254.369 14.9648 238.401C13.3532 227.727 10.8207 207.069 9.29541 192.28C8.02916 182.325 6.21611 163.279 5.18008 149.785C4.14406 140.635 2.8778 123.43 2.38857 111.288C1.72666 103.06 0.949641 87.9547 0.661856 77.5394C0.517963 70.2889 0.230177 57.428 -5.11937e-05 48.8253C0.201398 35.8206 0.287732 2.21545 0.31651 -1.38375e-08Z"
                                    fill="#244C42"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default MegaDropdown;
